(ns io.daskul.webapp.widgets.dashboard.time-series-chart
  "Time-series chart widget using Recharts.

   Renders a `:time-series` metric as a responsive combo chart: each series is a
   grouped bar or an overlaid line, chosen per-series via `:type` (\"bar\" |
   \"line\", default \"line\"). Bars sit side-by-side per period; lines draw on
   top. This reads a profit/expense trend far better than stacked translucent
   areas, whose overlap muddies correlated series (e.g. Net ⊂ Revenue).

   Expected metric shape:
   {:widget  :time-series
    :key     \"profit-trend\"
    :label   \"Profit Trend\"
    :series  [{:key :revenue  :label \"Revenue\"  :color \"green\" :type \"bar\"}
              {:key :expenses :label \"Expenses\" :color \"red\"   :type \"bar\"}
              {:key :net      :label \"Net\"      :color \"dark\"  :type \"line\"}]
    :data    [{:period \"2026-03\" :revenue 12000 :expenses 4000 :net 8000} ...]}"
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   ["recharts" :refer [ComposedChart Bar Line ReferenceLine
                       XAxis YAxis CartesianGrid Tooltip Legend]]))

;; Named colours → Mantine theme CSS vars, so the chart tracks the light/dark
;; theme like the rest of the page (a hardcoded hex would not). Recharts passes
;; these straight to the SVG stroke/fill; modern browsers resolve var() there.
(def ^:private color-map
  {"blue"   "var(--mantine-color-blue-6)"
   "red"    "var(--mantine-color-red-6)"
   "green"  "var(--mantine-color-green-6)"
   ;; Klei Purple — theme.cljs `brand` ramp, not Mantine's stock violet (#798).
   "brand"  "var(--mantine-color-brand-6)"
   "orange" "var(--mantine-color-orange-6)"
   "gray"   "var(--mantine-color-gray-6)"
   ;; the neutral "net" line — canonical text colour, so it's near-black in light
   ;; and near-white in dark (a fixed dark hex would vanish on a dark card).
   "dark"   "var(--mantine-color-text)"
   "teal"   "var(--mantine-color-teal-6)"
   "cyan"   "var(--mantine-color-cyan-6)"
   "pink"   "var(--mantine-color-pink-6)"
   "yellow" "var(--mantine-color-yellow-6)"})

(defn- resolve-color [c]
  (get color-map c c))

(def ^:private chart-height 280)

(defui time-series-chart
  "Render a time-series combo chart (grouped bars + overlaid lines)."
  [{:keys [label series data description]}]
  (let [chart-data (clj->js data)
        container-ref (uix/use-ref nil)
        [width set-width!] (uix/use-state 0)]
    ;; Measure container width via ResizeObserver
    (uix/use-effect
     (fn []
       (when-let [el @container-ref]
         (let [ro (js/ResizeObserver.
                   (fn [entries]
                     (let [entry (aget entries 0)
                           w (.-width (.-contentRect entry))]
                       (when (pos? w) (set-width! w)))))]
           (.observe ro el)
           #(.disconnect ro))))
     [])
    ($ ui/paper {:p "md" :radius "md" :withBorder true}
      ($ ui/group {:gap 6 :align "center" :mb "sm" :wrap "nowrap"}
        ($ ui/text {:fw 600 :size "sm"} label)
        (when description
          ($ ui/tooltip {:label description :multiline true :w 260
                         :position "top-end" :withArrow true}
            ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
              ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
      ($ :div {:ref container-ref :style {:width "100%" :height chart-height}}
        (when (pos? width)
          ;; barCategoryGap puts the empty space BETWEEN months; barGap keeps the
          ;; revenue/expenses pair tight within a month. (Don't cap bar width with
          ;; maxBarSize — with a cap, recharts left-aligns each narrow bar in its
          ;; own half-of-the-band slot, so the pair drifts far apart.)
          ($ ComposedChart {:data chart-data :width width :height chart-height
                            :barGap 2 :barCategoryGap "35%"
                            :margin #js {:top 5 :right 20 :bottom 5 :left 0}}
            ($ CartesianGrid {:strokeDasharray "3 3" :opacity 0.3})
            ($ XAxis {:dataKey "period" :fontSize 12 :tickMargin 8})
            ($ YAxis {:fontSize 12 :tickMargin 8})
            ($ Tooltip)
            ($ Legend)
            ;; zero baseline — so a negative Net (a loss) reads clearly against it
            ($ ReferenceLine {:y 0 :stroke "var(--mantine-color-gray-5)" :strokeWidth 1})
            ;; bars first so the line(s) draw on top; multiple bars group side-by-side.
            ;; isAnimationActive false: the chart width is measured after mount, and
            ;; that width change interrupts recharts' entry animation — it gets stuck
            ;; at frame 0 (0-height bars, undrawn line), so the marks never appear.
            (for [{:keys [key label color type]} series]
              (if (= type "bar")
                ($ Bar {:key      (name key)
                        :dataKey  (name key)
                        :name     label
                        :fill     (resolve-color color)
                        :radius   #js [3 3 0 0]
                        :isAnimationActive false})
                ($ Line {:key         (name key)
                         :dataKey     (name key)
                         :name        label
                         :type        "monotone"
                         :stroke      (resolve-color color)
                         :strokeWidth 2.4
                         :dot         false
                         :isAnimationActive false})))))))))
